import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { SvgIconComponent } from '@mui/icons-material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Typography from '../Typography';
import { COLORS } from '../../themev2';

export enum BannerType {
  Warning = 'warning',
  Error = 'error',
  Success = 'success',
  Info = 'info',
}

interface BannerPalette {
  background: string;
  border: string;
  text: string;
  Icon: SvgIconComponent;
}

const bannerPaletteMap: Record<BannerType, BannerPalette> = {
  [BannerType.Warning]: {
    background: COLORS.orange[100],
    border: COLORS.orange[700],
    text: COLORS.orange[900],
    Icon: WarningAmberRoundedIcon,
  },
  [BannerType.Error]: {
    background: COLORS.feedback.dangerBackground,
    border: COLORS.feedback.danger,
    text: COLORS.feedback.dangerDark,
    Icon: ErrorOutlineRoundedIcon,
  },
  [BannerType.Success]: {
    background: COLORS.feedback.successBackground,
    border: COLORS.green[700],
    text: COLORS.feedback.success,
    Icon: CheckCircleOutlineRoundedIcon,
  },
  [BannerType.Info]: {
    background: COLORS.cian[100],
    border: COLORS.cian[700],
    text: COLORS.cian[900],
    Icon: InfoOutlinedIcon,
  },
};

interface BannerProps {
  type: BannerType;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}

const Banner = ({ type, title, description, action }: BannerProps) => {
  const { background, border, text, Icon } = bannerPaletteMap[type];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: background,
        border: `1px solid ${border}`,
        borderRadius: '8px',
        padding: '12px 20px',
      }}
    >
      <Icon sx={{ color: text, flexShrink: 0 }} />
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: '20px', color: text }}>
          {title}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '20px', color: text }}>
            {description}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
};

export default Banner;
